<template>
  <div :class="$style.teacherAppPage">
    <div :class="$globalStyle.headerContainer">
      <div :class="$globalStyle.header">
        <div :class="$globalStyle.headerLeft">
          <img :src="saboteurLogo" alt="icon" :class="$globalStyle.headerLogo" />
          <div :class="$globalStyle.headerTitle">{{ $t("saboteur") }}</div>
        </div>
        <div :class="$globalStyle.headerRight">
          <div :class="[$globalStyle.headerButton]">
            <HeaderInnerButton text="Back" :clickFunction="goBack" buttonWidth="clamp(80px, 12vw, 100px)" />
          </div>
        </div>
      </div>
    </div>
    <div :class="$style.pageWrapper">
      <div :class="$style.bodyContainer">
        <div :class="$style.headerSection">
          <h1 :class="$style.title">Teacher Registration</h1>
          <p :class="$style.subtitle">Apply to join Saboteur as a teacher</p>
          <p :class="$style.description">
            Please fill out this form to apply for teacher access to Saboteur. All applications are reviewed by our administrative team.
          </p>
        </div>

      <div v-if="applicationStatus === 'pending'" :class="[$style.statusMessage, $style.statusPending]">
        <div :class="$style.statusIcon">⏳</div>
        <p :class="$style.statusText">Your application is pending review.</p>
        <p :class="$style.statusSubtext">We'll notify you once your application has been reviewed.</p>
      </div>
      <div v-else-if="applicationStatus === 'accepted'" :class="[$style.statusMessage, $style.statusApproved]">
        <div :class="$style.statusIcon">✓</div>
        <p :class="$style.statusText">Your application has been approved!</p>
        <p :class="$style.statusSubtext">You now have teacher access to Saboteur.</p>
      </div>
      <div v-else-if="applicationStatus === 'declined'" :class="[$style.statusMessage, $style.statusRejected]">
        <div :class="$style.statusIcon">✗</div>
        <p :class="$style.statusText">Your application was rejected.</p>
        <p :class="$style.statusSubtext">Please contact support if you have questions.</p>
      </div>

      <div v-if="!applicationStatus || applicationStatus === 'declined'" :class="$style.formContainer">
        <div :class="$style.formSection">
          <h2 :class="$style.sectionTitle">Personal Information</h2>
          <div :class="$style.form">
            <div :class="$style.formRowName" style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; width: 50%; margin-left: auto; margin-right: auto;">
              <div :class="$style.formField">
                <AuthModalInput
                  label="First Name *"
                  :value="form.firstName"
                  inputType="text"
                  placeholder="Enter your first name"
                  @update:value="form.firstName = $event"
                />
              </div>
              <div :class="$style.formField">
                <AuthModalInput
                  label="Last Name *"
                  :value="form.lastName"
                  inputType="text"
                  placeholder="Enter your last name"
                  @update:value="form.lastName = $event"
                />
              </div>
            </div>
            <div :class="$style.formFieldFull" style="width: 50%; margin-left: auto; margin-right: auto; margin-top: 16px;">
              <AuthModalInput
                label="Email Address *"
                :value="userEmail"
                inputType="email"
                placeholder="Email from your authenticated account"
                :disabled="true"
                :isDisabled="true"
              />
              <p :class="$style.emailHelper">
                <span v-if="!isLoggedIn" :class="$style.loginWarning">⚠️ Please log in to associate this application with your account.</span>
                <span v-else>Email from your authenticated account</span>
              </p>
            </div>
            <div :class="$style.formRow" style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; width: 50%; margin-left: auto; margin-right: auto; margin-top: 16px;">
              <div :class="$style.formField">
                <AuthModalInput
                  ref="dobInput"
                  label="Date of Birth *"
                  :value="form.dateOfBirth"
                  inputType="text"
                  placeholder="mm/dd/yyyy"
                  @update:value="formatDateOfBirth"
                />
              </div>
              <div :class="$style.formField">
                <AuthModalInput
                  ref="phoneInput"
                  label="Phone Number *"
                  :value="form.phoneNumber"
                  inputType="tel"
                  placeholder="(000)-0000-000"
                  @update:value="formatPhoneNumber"
                />
              </div>
            </div>
          </div>
        </div>

        <div :class="$style.formSection">
          <h2 :class="$style.sectionTitle">Institution Information</h2>
          <div :class="$style.form">
            <div :class="$style.formFieldFull" style="width: 50%; margin-left: auto; margin-right: auto; margin-top: 16px;">
              <AuthModalInput
                label="Institution/University *"
                :value="form.institution"
                inputType="text"
                placeholder="e.g., University of South Florida, etc."
                @update:value="form.institution = $event"
              />
            </div>
            <div :class="$style.formFieldFull" style="width: 50%; margin-left: auto; margin-right: auto; margin-top: 16px;">
              <AuthModalInput
                label="Position/Role *"
                :value="form.positionRole"
                inputType="text"
                placeholder="e.g., Professor, Teacher, Graduate Student, etc."
                @update:value="form.positionRole = $event"
              />
            </div>
            <div :class="[$style.formFieldFull, $style.descriptionField]" style="width: 50%; margin-left: auto; margin-right: auto; margin-top: 16px; margin-bottom: 24px;">
              <AuthModalInput
                label="Description (Optional)"
                :value="form.description"
                placeholder="Tell us about your teaching background, current projects, or why you need access to Saboteur..."
                :isTextArea="true"
                @update:value="form.description = $event"
              />
            </div>
          </div>
        </div>

        <div v-if="errorMessage" :class="$style.errorMessage">
          {{ errorMessage }}
        </div>

        <div :class="$style.ctaContainer">
          <UtilButton
            :text="isLoading ? 'Submitting...' : 'Submit Application'"
            :clickFunction="submitApplication"
            :disabled="isLoading"
            backBg="#112d4e"
            frontBg="#3d7cb8"
            textColor="#FFFFFF"
            buttonWidth="clamp(200px, 25vw, 280px)"
          />
        </div>

        <div :class="$style.agreementContainer">
          <p :class="$style.agreement">
            By submitting this application, you agree to use Saboteur in accordance with educational ethics guidelines and institutional policies.
          </p>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import styles from "@/css/HomeView.module.css";
import globalStyles from "@/css/global.module.css";
import SaboteurLogo from "../assets/saboteur-logo.svg";
import HeaderInnerButton from "@/components/buttons/HeaderInnerButton.vue";
import AuthModalInput from "@/components/auth/AuthModalInput.vue";
import UtilButton from "@/components/util/UtilButton.vue";
import gameService from "@/services/game.service";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "vue3-toastify";

export default {
  name: "TeacherApplicationView",
  components: { HeaderInnerButton, AuthModalInput, UtilButton },
  data() {
    return {
      saboteurLogo: SaboteurLogo,
      form: {
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        phoneNumber: "",
        institution: "",
        positionRole: "",
        description: "",
      },
      isLoading: false,
      errorMessage: "",
      applicationStatus: null,
    };
  },
  computed: {
    $style() {
      return styles;
    },
    $globalStyle() {
      return globalStyles;
    },
    userEmail() {
      return this.user?.email || "";
    },
  },
  methods: {
    normalizeApplicationStatus(status) {
      const raw = (status || "").toString().trim().toLowerCase();
      if (raw === "approved") return "accepted";
      if (raw === "rejected") return "declined";
      return raw;
    },
    populateUserData() {
      // Auto-populate form with logged-in user's data
      if (this.user && this.user.firstName && !this.form.firstName) {
        this.form.firstName = this.user.firstName;
      }
      if (this.user && this.user.lastName && !this.form.lastName) {
        this.form.lastName = this.user.lastName;
      }
    },
    setupInputValidation() {
      // Add keypress validation for Date of Birth and Phone Number inputs
      this.$nextTick(() => {
        // Find Date of Birth input by label
        const dobLabel = Array.from(document.querySelectorAll('label')).find(
          el => el.textContent && el.textContent.includes('Date of Birth')
        );
        if (dobLabel) {
          const dobContainer = dobLabel.closest('.util-input');
          const dobInput = dobContainer?.querySelector('input');
          if (dobInput) {
            dobInput.addEventListener('keypress', (e) => {
              const char = String.fromCharCode(e.which);
              if (!/[0-9]/.test(char)) {
                e.preventDefault();
              }
            });
            dobInput.addEventListener('paste', (e) => {
              e.preventDefault();
              const paste = (e.clipboardData || window.clipboardData).getData('text');
              const numbers = paste.replace(/\D/g, '');
              if (numbers) {
                this.formatDateOfBirth(numbers);
              }
            });
            dobInput.addEventListener('input', (e) => {
              // Double-check and strip any non-numeric that might have gotten through
              const value = e.target.value;
              const numbers = value.replace(/\D/g, '');
              if (value !== numbers && numbers.length > 0) {
                this.formatDateOfBirth(numbers);
              }
            });
          }
        }
        
        // Find Phone Number input by label
        const phoneLabel = Array.from(document.querySelectorAll('label')).find(
          el => el.textContent && el.textContent.includes('Phone Number')
        );
        if (phoneLabel) {
          const phoneContainer = phoneLabel.closest('.util-input');
          const phoneInput = phoneContainer?.querySelector('input');
          if (phoneInput) {
            phoneInput.addEventListener('keypress', (e) => {
              const char = String.fromCharCode(e.which);
              if (!/[0-9]/.test(char)) {
                e.preventDefault();
              }
            });
            phoneInput.addEventListener('paste', (e) => {
              e.preventDefault();
              const paste = (e.clipboardData || window.clipboardData).getData('text');
              const numbers = paste.replace(/\D/g, '');
              if (numbers) {
                this.formatPhoneNumber(numbers);
              }
            });
            phoneInput.addEventListener('input', (e) => {
              // Double-check and strip any non-numeric that might have gotten through
              const value = e.target.value;
              const numbers = value.replace(/\D/g, '');
              if (value !== numbers && numbers.length > 0) {
                this.formatPhoneNumber(numbers);
              }
            });
          }
        }
      });
    },
    formatDateOfBirth(value) {
      // Remove all non-numeric characters immediately
      let numbers = value.replace(/\D/g, "");
      
      // Limit to 8 digits (MMDDYYYY)
      if (numbers.length > 8) {
        numbers = numbers.slice(0, 8);
      }
      
      // Format as MM/DD/YYYY with validation
      let formatted = "";
      
      if (numbers.length === 0) {
        formatted = "";
      } else if (numbers.length <= 2) {
        // First 2 digits: validate month (01-12)
        const month = parseInt(numbers);
        if (month > 12) {
          // If first digit is 2-9, only allow 0-2 for first digit
          if (numbers.length === 1 && parseInt(numbers[0]) > 1) {
            numbers = "";
            formatted = "";
          } else {
            // Keep only valid first digit
            numbers = numbers.slice(0, 1);
            formatted = numbers;
          }
        } else {
          formatted = numbers;
        }
      } else if (numbers.length <= 4) {
        // First 2 digits: month, next 2: day
        const month = parseInt(numbers.slice(0, 2));
        const day = parseInt(numbers.slice(2));
        
        if (month > 12 || month === 0) {
          // Invalid month
          numbers = numbers.slice(0, 1);
          formatted = numbers;
        } else {
          formatted = numbers.slice(0, 2) + "/";
          if (numbers.length === 4) {
            if (day > 31 || day === 0) {
              // Invalid day, keep only 3 digits
              numbers = numbers.slice(0, 3);
              formatted = numbers.slice(0, 2) + "/" + numbers.slice(2);
            } else {
              formatted = numbers.slice(0, 2) + "/" + numbers.slice(2, 4);
            }
          } else {
            formatted += numbers.slice(2);
          }
        }
      } else {
        // Full date: MM/DD/YYYY
        const month = parseInt(numbers.slice(0, 2));
        const day = parseInt(numbers.slice(2, 4));
        const year = parseInt(numbers.slice(4));
        
        if (month > 12 || month === 0) {
          numbers = numbers.slice(0, 1);
          formatted = numbers;
        } else if (day > 31 || day === 0) {
          numbers = numbers.slice(0, 3);
          formatted = numbers.slice(0, 2) + "/" + numbers.slice(2);
        } else {
          formatted = numbers.slice(0, 2) + "/" + numbers.slice(2, 4) + "/" + numbers.slice(4);
          // Validate year: 1900-2024
          if (numbers.length === 8) {
            if (year < 1900 || year > 2024) {
              // Invalid year, keep only 7 digits
              numbers = numbers.slice(0, 7);
              formatted = numbers.slice(0, 2) + "/" + numbers.slice(2, 4) + "/" + numbers.slice(4);
            }
          }
        }
      }
      
      this.form.dateOfBirth = formatted;
    },
    formatPhoneNumber(value) {
      // Remove all non-numeric characters
      let numbers = value.replace(/\D/g, "");
      
      // Limit to 10 digits
      if (numbers.length > 10) {
        numbers = numbers.slice(0, 10);
      }
      
      // Format as (000)-0000-000
      let formatted = numbers;
      if (numbers.length > 0) {
        formatted = "(" + numbers.slice(0, 3);
        if (numbers.length > 3) {
          formatted += ")-" + numbers.slice(3, 6);
          if (numbers.length > 6) {
            formatted += "-" + numbers.slice(6);
          }
        } else {
          formatted += ")";
        }
      }
      
      this.form.phoneNumber = formatted;
    },
    goBack() {
      this.$router.push({ name: "home" });
    },
    async loadApplicationStatus() {
      // Only load if user is logged in
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return;
      }
      
      try {
        const response = await gameService.getApplicationStatus();
        if (response && response.hasApplication) {
          this.applicationStatus = this.normalizeApplicationStatus(response.status);
        }
      } catch (error) {
        // Silently handle errors
      }
    },
    async submitApplication() {
      this.errorMessage = "";

      const token = localStorage.getItem('accessToken')
      
      if (!token) {
        this.errorMessage = "Please log in to submit your application.";
        toast.error("Please log in to submit your application.");
        this.$router.push({ name: "home" });
        setTimeout(() => {
          this.authStore.showAuthModal("login");
        }, 500);
        return;
      }

      if (!this.form.firstName && this.user && this.user.firstName) {
        this.form.firstName = this.user.firstName;
      }
      if (!this.form.lastName && this.user && this.user.lastName) {
        this.form.lastName = this.user.lastName;
      }

      if (!this.form.firstName || !this.form.lastName || !this.form.dateOfBirth || 
          !this.form.phoneNumber || !this.form.institution || !this.form.positionRole) {
        this.errorMessage = "Please fill in all required fields.";
        return;
      }

      this.isLoading = true;
      try {
        const response = await gameService.applyTeacher({
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          dateOfBirth: this.form.dateOfBirth,
          phoneNumber: this.form.phoneNumber,
          institution: this.form.institution,
          positionRole: this.form.positionRole,
          description: this.form.description || null,
        });

        if (response && response.success) {
          toast.success("Application submitted successfully!");
          this.applicationStatus = this.normalizeApplicationStatus(response.status || "pending");
          this.form = {
            firstName: "",
            lastName: "",
            dateOfBirth: "",
            phoneNumber: "",
            institution: "",
            positionRole: "",
            description: "",
          };
        } else {
          this.errorMessage = response?.message || "Failed to submit application. Please try again.";
          if (response?.message && response.message.includes("log in")) {
            toast.error("Please log in to submit your application.");
            setTimeout(() => {
              this.authStore.showAuthModal("login");
            }, 2000);
          }
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.errorMessage = error.response.data.message || "Failed to submit application.";
          if (error.response.data.message && error.response.data.message.includes("log in")) {
            toast.error("Please log in to submit your application.");
            setTimeout(() => {
              this.authStore.showAuthModal("login");
            }, 2000);
          }
        } else {
          this.errorMessage = "An error occurred. Please try again.";
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    user: {
      handler(newUser) {
        if (newUser && !this.applicationStatus) {
          this.populateUserData();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    // Check token directly from localStorage (route guard should handle this, but double-check)
    const token = localStorage.getItem('accessToken')
    if (!token) {
      this.$router.push({ name: "home" });
      setTimeout(() => {
        this.authStore.showAuthModal("login");
      }, 500);
      return;
    }
    
    // If user data is not loaded yet, fetch it
    if (!this.user || !this.user.email) {
      await this.authStore.fetchUser();
    }
    
    await this.loadApplicationStatus();
    this.setupInputValidation();
    this.populateUserData();
  },
  setup() {
    const authStore = useAuthStore();
    const { user, isLoggedIn } = storeToRefs(authStore);
    return { user, isLoggedIn, authStore };
  },
};
</script>

<style module>
.teacherAppPage {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding-bottom: 40px;
  width: 100%;
  overflow-x: hidden;
}

.pageWrapper {
  width: 100%;
  max-width: 100vw;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;
  box-sizing: border-box;
}

.bodyContainer {
  width: min(100%, 500px);
  margin: 0 auto;
  padding: 30px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.headerSection {
  text-align: center;
  margin-bottom: 40px;
  width: min(100%, 500px);
  align-self: center;
}

.title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 12px;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.subtitle {
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 12px;
  text-align: center;
  font-weight: 500;
}

.description {
  font-size: 1rem;
  color: #f0f0f0;
  margin-bottom: 0;
  text-align: center;
  line-height: 1.6;
  width: min(100%, 500px);
  margin-left: auto;
  margin-right: auto;
}

.formContainer {
  background: #ffffff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
  width: min(100%, 500px);
  box-sizing: border-box;
  align-self: center;
}

.formSection {
  margin-bottom: 32px;
}

.formSection:last-of-type {
  margin-bottom: 24px;
}

.sectionTitle {
  font-size: 1.2rem;
  font-weight: 600;
  color: #A50000;
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 2px solid #A50000;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.formRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
}

.formRowName {
  display: grid;
  grid-template-columns: 1fr 3fr !important;
  gap: 16px;
  width: 100%;
}

.formRowName > .formField {
  min-width: 0;
  overflow: hidden;
}

.formRowName > .formField:first-child {
  grid-column: 1;
}

.formRowName > .formField:last-child {
  grid-column: 2;
}

.formField {
  flex: 1;
  min-width: 0;
}

.formField :deep(.util-input) {
  width: 100%;
  max-width: 100%;
}

.formFieldFull {
  width: 100%;
}

.formFieldFull :deep(.util-input) {
  width: 100%;
  max-width: 100%;
}

.descriptionField :deep(.input-container) {
  height: 144px !important;
}

.emailHelper {
  font-size: 0.85rem;
  color: #666;
  margin-top: 4px;
  margin-left: 4px;
  font-style: italic;
}

.loginWarning {
  color: #d32f2f;
  font-weight: 500;
  font-style: normal;
}

.textAreaContainer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
}

.label {
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin-bottom: 8px;
}

.textAreaWrapper {
  display: flex;
  align-items: flex-start;
  min-height: 100px;
  border-radius: 8px;
  padding: 12px 14px;
  background-color: #ededed;
  box-sizing: border-box;
  transition: background-color 0.2s ease;
}

.textAreaWrapper:focus-within {
  background-color: #fff;
  box-shadow: 0 0 0 2px rgba(165, 0, 0, 0.1);
}

.textArea {
  width: 100%;
  padding: 0;
  border: none;
  border-radius: 0;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  background: transparent;
  outline: none;
  box-sizing: border-box;
  line-height: 1.5;
}

.textArea::placeholder {
  color: #999;
}

.errorMessage {
  color: #d32f2f;
  background: #ffebee;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: center;
  border-left: 4px solid #d32f2f;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.errorMessage::before {
  content: "⚠";
  font-size: 1.2rem;
}

.statusMessage {
  background: #ffffff;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-left: 5px solid;
  width: min(100%, 500px);
  margin-left: auto;
  margin-right: auto;
  align-self: center;
}

.statusPending {
  border-left-color: #ff9800;
  background: linear-gradient(135deg, #fff3e0 0%, #ffffff 100%);
}

.statusApproved {
  border-left-color: #4caf50;
  background: linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%);
}

.statusRejected {
  border-left-color: #f44336;
  background: linear-gradient(135deg, #ffebee 0%, #ffffff 100%);
}

.statusIcon {
  font-size: 3rem;
  margin-bottom: 12px;
  line-height: 1;
}

.statusText {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
}

.statusSubtext {
  font-size: 0.95rem;
  color: #666;
  margin: 0;
}

.ctaContainer {
  display: flex;
  justify-content: center;
  margin: 50px 0 30px 0;
}

.agreementContainer {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-top: 30px;
  border: 1px solid #e0e0e0;
}

.agreement {
  font-size: 0.85rem;
  color: #666;
  text-align: center;
  margin: 0;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .bodyContainer {
    padding: 20px 16px;
    max-width: 100%;
  }

  .formContainer {
    padding: 24px 20px;
    border-radius: 12px;
    max-width: 100%;
  }

  .formRow {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .formField :deep(.util-input),
  .formFieldFull :deep(.util-input) {
    width: 100%;
  }

  .title {
    font-size: 1.8rem;
  }

  .subtitle {
    font-size: 1.1rem;
  }

  .description {
    font-size: 0.9rem;
  }

  .formSection {
    margin-bottom: 28px;
  }

  .sectionTitle {
    font-size: 1.1rem;
  }

  .statusMessage {
    padding: 24px 20px;
  }

  .statusIcon {
    font-size: 2.5rem;
  }

  .statusText {
    font-size: 1.1rem;
  }
}
</style>
